import { ForwardedRef, useEffect, useState } from 'react';
import { Box } from '../Box';
import { ModalBase, ModalBaseProps } from '../Modal';
import { Stack } from '../Stack';
import { useContainerContext } from './ContainerContextProvider';

export interface ContainerModalProps extends Pick<ModalBaseProps, 'title'> {
  /**
   * Additional actions to be displayed in the modal. These will be placed in the middle in the
   * area above the container in the modal. The additional actions are automatically wrapped in a
   * box with flexbox for consistent layout and spacing.
   *
   * Note that these are **additional** actions to show only in the modal. The `Container` will
   * continue to render any actions defined in the top-level `actions` prop.
   */
  additionalActions?: React.ReactNode;

  /**
   * Children are optional because they are not needed when using portals, which instead
   * inject the content by moving the dom node containing the portal.
   */
  children?: React.ReactNode;

  /**
   * Returns a ref to the parent element for the container when rendered inside the
   * modal. Needed to enable the portal strategy.
   */
  containerParentRef?: ForwardedRef<HTMLDivElement>;

  /**
   * Filter controls to be displayed in the modal. These will be placed to the left in the
   * area above the container in the modal. The filters are automatically wrapped in a
   * box with flexbox for consistent layout and spacing.
   */
  filters?: React.ReactNode;

  /**
   * Time range related controls to be displayed in the modal. These will be place to the right in
   * the area above the container in the modal.
   */
  timeRangeControls?: React.ReactNode;
}

export const ContainerModal = ({
  title,
  children,
  additionalActions,
  filters,
  timeRangeControls,
  containerParentRef,
}: ContainerModalProps) => {
  const { onCloseModal } = useContainerContext();

  const hasAdditionalContent = !!additionalActions || !!filters || !!timeRangeControls;

  return (
    <ModalBase open={true} title={title} onClose={onCloseModal} size="fullScreen">
      <Box
        sx={(theme) => {
          return {
            padding: theme.spacing(2),
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),

            ['.OpenInModalAction']: {
              display: 'none',
            },
          };
        }}
        className="ContainerModal"
        data-testid="container-modal"
      >
        {hasAdditionalContent && (
          <Stack
            spacing={1}
            direction="row"
            sx={{
              display: 'flex',
              width: '100%',
            }}
          >
            <Box
              sx={{
                // Enables this item to be "greedier" about grabbing space than
                // the additional actions.
                flex: '1 1 auto',
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: (theme) => theme.spacing(1),
              }}
            >
              {!!filters && filters}
            </Box>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                alignItems: 'flex-start',
                gap: (theme) => theme.spacing(1),
              }}
            >
              {!!additionalActions && additionalActions}
            </Box>
            <div>{!!timeRangeControls && timeRangeControls}</div>
          </Stack>
        )}
        <Box
          sx={{
            flex: 1,
            // Forces flexbox to stay inside parent (intead of greedily growing), so we have
            // scrolling inside the container body (instead of the whole modal).
            height: 0,
          }}
          ref={containerParentRef}
        >
          {children}
        </Box>
      </Box>
    </ModalBase>
  );
};

export interface ContainerModalWithPortalProps extends ContainerModalProps {
  /**
   * The dom node that contains the container content when rendered inline.
   */
  inlineContainerParentEl: HTMLDivElement;

  /**
   * The dom node that contains the portal with the container content.
   */
  portalEl: HTMLDivElement;
}

/**
 * Moves the specified child element to the specified parent element unless the
 * child is already there.
 */
function reparentElement(childEl: HTMLElement, parentEl: HTMLElement) {
  if (childEl.parentElement === parentEl) {
    // The child already has the correct parent. Do nothing.
    return;
  }

  // Note: appending is safe because we intentionally set up the parents of
  // the two potential locations for the portal to have the portal as their
  // only child. If this changes, this code will need to become more complex.
  parentEl.appendChild(childEl);
}

/**
 * Small wrapper around `ContainerModal` that adds the extra code needed to
 * make the portal strategy work.
 */
export const ContainerModalWithPortal = ({
  portalEl,
  inlineContainerParentEl,
  ...otherProps
}: ContainerModalWithPortalProps) => {
  const [modalContainerParentEl, setModalContainerParentEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (modalContainerParentEl) {
      // When the modal is open, move the content to the modal.
      reparentElement(portalEl, modalContainerParentEl);
    }

    return () => {
      reparentElement(portalEl, inlineContainerParentEl);
    };
  }, [portalEl, modalContainerParentEl, inlineContainerParentEl]);

  return <ContainerModal {...otherProps} containerParentRef={setModalContainerParentEl} />;
};
